import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { authAPI } from '../../services/api';
import { eventToRouteMap } from '../../Utils/constants';
import { getStoredQueryParams } from '../../Utils/utm';
import { useAuth } from '../../Context/AuthContext';
import JourneyLayout from '../../components/JourneyLayout';

function OTP() {
  const [otp, setOtp] = useState(['', '', '', '']);
  const [errors, setErrors] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const hasSubmittedRef = useRef(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const phoneNumber = location.state?.phoneNumber || localStorage.getItem('phone_number') || '';
  const profileId = location.state?.profileId || localStorage.getItem('profile_id') || '';

  useEffect(() => {
    // Check if user is already logged in
    const checkLoginStatus = async () => {
      setCheckingAuth(true);
      const token = localStorage.getItem('token') || 
                    localStorage.getItem('authToken') || 
                    localStorage.getItem('authtoken');
      
      if (token && isAuthenticated) {
        // User is logged in, fetch phone number
        const storedProfileId = localStorage.getItem('profile_id') || '';
        const storedPhone = localStorage.getItem('phone_number') || phoneNumber;
        
        if (storedProfileId) {
          try {
            const response = await authAPI.getDashboardData(storedProfileId);
            if (response.Status === 1) {
              const phone = response.Data?.profile_details?.mobile || storedPhone;
              setVerifiedPhone(phone);
              // Get next_event_name for navigation
              const eventName = response.Data?.next_event_name;
              if (eventName) {
                navigate(eventToRouteMap[eventName]);
              }
            }
          } catch (error) {
            console.error('Error fetching dashboard data:', error);
            // Fallback to stored phone number
            if (storedPhone) {
              setVerifiedPhone(storedPhone);
            }
          }
        } else if (storedPhone) {
          setVerifiedPhone(storedPhone);
        }
      }
      setCheckingAuth(false);
    };

    checkLoginStatus();
  }, [isAuthenticated, phoneNumber, navigate]);

  useEffect(() => {
    // Focus first input on mount (only if not logged in)
    if (!isAuthenticated && inputRefs[0].current) {
      inputRefs[0].current.focus();
    }

    // Start timer on mount (only if not logged in)
    if (!isAuthenticated) {
      setCanResend(false);
      setTimer(30);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Timer countdown effect
  useEffect(() => {
    if (timer > 0 && !canResend) {
      const interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            setCanResend(true);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [timer, canResend]);

  const handleChange = (index, e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length > 1) {
      // Handle paste - split into individual digits
      const digits = value.split('').slice(0, 4);
      const newOtp = [...otp];
      digits.forEach((digit, i) => {
        if (index + i < 4) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);
      // Focus the last filled input or next empty
      const nextIndex = Math.min(index + digits.length, 3);
      if (inputRefs[nextIndex]?.current) {
        inputRefs[nextIndex].current.focus();
      }
    } else {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      setErrors('');

      // Auto-focus next input if value entered
      if (value && index < 3 && inputRefs[index + 1]?.current) {
        inputRefs[index + 1].current.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim().replace(/\D/g, '').slice(0, 4);
    const newOtp = ['', '', '', ''];
    pastedData.split('').forEach((digit, i) => {
      if (i < 4) {
        newOtp[i] = digit;
      }
    });
    setOtp(newOtp);
    setErrors('');
    // Focus the last input or next empty
    const focusIndex = Math.min(pastedData.length, 3);
    if (inputRefs[focusIndex]?.current) {
      inputRefs[focusIndex].current.focus();
    }
  };

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        resolve({ lat: '', lng: '' });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude.toString(),
            lng: position.coords.longitude.toString(),
          });
        },
        (error) => {
          // If geolocation fails, use empty strings
          console.warn('Geolocation error:', error);
          resolve({ lat: '', lng: '' });
        }
      );
    });
  };

  const handleSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();
    const otpString = otp.join('');

    if (otpString.length !== 4) {
      setErrors('Please enter the complete 4-digit OTP');
      return;
    }

    if (!/^\d{4}$/.test(otpString)) {
      setErrors('Please enter a valid OTP (only digits allowed)');
      return;
    }

    if (!profileId) {
      setErrors('Session expired. Please start again.');
      setTimeout(() => navigate('/apply-now'), 2000);
      return;
    }

    // Prevent duplicate submissions
    if (hasSubmittedRef.current || loading) {
      return;
    }

    hasSubmittedRef.current = true;
    setLoading(true);
    setErrors('');

    try {
      // Get current location
      const location = await getCurrentLocation();

      // Call verifyOTP API
      const response = await authAPI.verifyOTP({
        profile_id: profileId,
        otp: otpString,
        geo_lat: location.lat,
        geo_long: location.lng,
      });

      if (response.Status === 1) {
        // Store profile_id if available
        if (response.Data?.cust_profile_id) {
          localStorage.setItem('profile_id', response.Data.cust_profile_id);
        }

        // Store auth token if provided
        if (response.Data?.app_login_token) {
          localStorage.setItem('token', response.Data.app_login_token);
          localStorage.setItem('authToken', response.Data.app_login_token);
          // Login using AuthContext
          login(response.Data.app_login_token);
        }

        // Fire Facebook Pixel tracking after successful OTP verification
        if (window.fbq) {
          window.fbq("track", "Lead", {
            content_name: "OTP Verified - Loan Application",
            content_category: "Lead Generation",
          });
        }

        // Fire vCommission tracking pixel after successful OTP verification
        const uniqueId = response.Data?.cust_profile_id || localStorage.getItem("profile_id");
        console.log("uniqueId", uniqueId);
        if (uniqueId) {
          console.log("uniqueId in if", uniqueId);
          const trackingPixel = document.createElement("iframe");
          trackingPixel.src = `https://track.vcommission.com/pixel?av=6925699243976c5d6f75da12&txn_id=${uniqueId}&sub1=${uniqueId}`;
          trackingPixel.scrolling = "no";
          trackingPixel.frameBorder = "0";
          trackingPixel.width = "1";
          trackingPixel.height = "1";
          trackingPixel.style.display = "none";
          document.body.appendChild(trackingPixel);
          console.log("trackingPixel", trackingPixel);
        }

        // Fire AdsPlay affiliate install tracking after OTP verification (if clickid present)
        const searchParams = new URLSearchParams(location.search);
        const storedParams = getStoredQueryParams();
        const clickid = searchParams.get("clickid") || searchParams.get("click_id") || storedParams.clickid || "";
        if (clickid) {
          const adsplayPixel = document.createElement("img");
          adsplayPixel.src = `https://affiliates.adsplay.in/trackingcode_installs.php?clickid=${encodeURIComponent(clickid)}`;
          adsplayPixel.width = 1;
          adsplayPixel.height = 1;
          adsplayPixel.style.display = "none";
          adsplayPixel.alt = "";
          document.body.appendChild(adsplayPixel);
        }

        // Check next_step from response and navigate accordingly
        const nextStep = response.Data?.next_step;
        if (nextStep && eventToRouteMap[nextStep]) {
          navigate(eventToRouteMap[nextStep]);
        } else {
          // Default: Navigate to PAN details page
          navigate('/pan-details');
        }
      } else {
        // Clear OTP boxes on error
        setOtp(['', '', '', '']);
        setErrors(response.Message || 'OTP verification failed. Please try again.');
        // Focus first input
        if (inputRefs[0].current) {
          inputRefs[0].current.focus();
        }
      }
    } catch (error) {
      // Clear OTP boxes on error
      setOtp(['', '', '', '']);
      setErrors(error.message || 'Failed to verify OTP. Please try again.');
      // Focus first input
      if (inputRefs[0].current) {
        inputRefs[0].current.focus();
      }
    } finally {
      setLoading(false);
      hasSubmittedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, profileId, navigate, login, loading]);

  // Auto-submit when all 4 digits are entered
  useEffect(() => {
    const otpString = otp.join('');
    if (otpString.length === 4 && !loading && !hasSubmittedRef.current) {
      handleSubmit();
    }
  }, [otp, loading, handleSubmit]);

  const handleResend = async () => {
    if (!phoneNumber) {
      setErrors('Phone number not found. Please start again.');
      setTimeout(() => navigate('/apply-now'), 2000);
      return;
    }

    setResendLoading(true);
    setErrors('');

    try {
      const response = await authAPI.resendOTP({
        mobile: phoneNumber,
        utm_source: '',
        utm_medium: '',
        utm_campaign: '',
        utm_content: '',
        utm_term: '',
      });

      if (response.Status === 1) {
        // Update profile_id if provided
        if (response.Data?.cust_profile_id) {
          localStorage.setItem('profile_id', response.Data.cust_profile_id);
        }

        // Reset OTP field
        setOtp(['', '', '', '']);
        hasSubmittedRef.current = false;
        if (inputRefs[0].current) {
          inputRefs[0].current.focus();
        }

        // Reset timer
        setTimer(30);
        setCanResend(false);

        // Show success message
        alert('OTP has been resent successfully!');
      } else {
        setErrors(response.Message || 'Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      setErrors(error.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleContinueFlow = () => {
    navigate('/my-dashboard');
  };

  return (
    <JourneyLayout journeyStep={1}>
      <button
        onClick={() => navigate('/apply-now')}
        className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-theme mb-6 transition-colors bg-transparent border-none p-0"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back</span>
      </button>

      {/* Verified User Card - Show when logged in */}
      {!checkingAuth && verifiedPhone && (
        <div className="bg-theme border border-theme/20 rounded-2xl p-8 sm:p-10 shadow-lg mb-6">
          <div className="flex flex-col items-center text-center space-y-5">
            <div className="bg-white p-3 rounded-full">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-bold text-white">
                Your Mobile is Verified
              </h3>
              <p className="text-white/90 text-base">
                Your number <span className="font-semibold">+91 {verifiedPhone}</span> is already verified.
              </p>
              <p className="text-white/80 text-sm mt-3">
                Please move forward in the flow
              </p>
            </div>
            <button
              onClick={handleContinueFlow}
              className="px-8 py-3 bg-white text-theme rounded-xl font-semibold hover:bg-gray-50 transition-all text-base shadow-md hover:shadow-lg mt-2"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* OTP Form - Only show when NOT logged in */}
      {!checkingAuth && !verifiedPhone && (
        <>
          <div className="text-center space-y-3 mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Verify the OTP
            </h1>
            <p className="text-gray-600">
              Enter the code sent to{' '}
              <span className="text-gray-900 font-semibold">
                +91 {phoneNumber || 'your number'}
              </span>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold tracking-wide text-gray-500 mb-3 text-center uppercase">
                4 Digit Secure Code
              </label>
              <div className="flex justify-center gap-3 sm:gap-4 mb-3">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={inputRefs[index]}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleChange(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    className={`w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-2xl bg-white border-2 text-center text-2xl sm:text-3xl font-bold tracking-widest transition-all ${
                      errors
                        ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                        : 'border-gray-300 focus:border-theme focus:ring-2 focus:ring-theme/20'
                    } focus:outline-none hover:border-theme/50`}
                  />
                ))}
              </div>
              {errors && (
                <p className="text-xs text-red-500 text-center mt-2 font-medium">{errors}</p>
              )}
            </div>

            <div className="text-center">
              {canResend ? (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendLoading}
                  className="resend-otp-button text-sm font-semibold text-theme hover:text-theme2 transition-colors disabled:opacity-50 cursor-pointer underline hover:no-underline"
                >
                  {resendLoading ? 'Resending OTP...' : "Didn't get the code? Resend"}
                </button>
              ) : (
                <p className="text-sm text-gray-500">
                  Resend available in{' '}
                  <span className="text-theme font-semibold">{timer}s</span>
                </p>
              )}
            </div>

            {loading && (
              <div className="w-full bg-gradient-to-r from-theme to-theme2 text-white px-6 py-4 rounded-2xl font-semibold flex items-center justify-center gap-2 shadow-lg">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Verifying...</span>
              </div>
            )}
          </form>
        </>
      )}
    </JourneyLayout>
  );
}

export default OTP;


